import { useState, useEffect, type ReactNode } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { Schema as S } from "effect";
import { EmailAddressValue } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
import { useTranslation } from "react-i18next";
import { cn } from "@ascleo/ui/lib/utils";
import {
  AlertTriangle,
  CircleAlert,
  ClipboardList,
  Gauge,
  Mail,
  MailCheck,
  Pill,
  Send,
  ShieldAlert,
  Square,
  SquareCheck,
  type LucideIcon,
} from "lucide-react";
import {
  usePrescriptionFlowSend,
  usePrescriptionFlowSelector,
} from "../context";
import { prescriptionFlowSelectors } from "../selectors";
import {
  kioskOperationsClient,
  type KioskOperationsClient,
} from "../../../../../lib/rpc/kiosk-operations.client";
import { usePlatform } from "../../../../../platform/context";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskTextField } from "../../../../../components/kiosk-field";
import { useStepPrimaryAction } from "../../../context";
import { reportOperationalError } from "../../../../../lib/operational-error-telemetry";
import { useAudio } from "../../../../../lib/use-audio";
import { ManualIdentificationNotice } from "./ManualIdentificationNotice";

const CompletionEmailChoice = S.Union(
  [S.Struct({
    requested: S.Literal(false),
    email: S.Literal(""),
  }),
  S.Struct({
    requested: S.Literal(true),
    email: EmailAddressValue,
  })],
);

/**
 * Request review step — shows a summary of the prescription request before
 * submission: the chosen reason + urgency and the free-text medication request,
 * plus a quarterly eGK warning if applicable. "Absenden" sits in the footer
 * next to the footer "Zurück" (KO-06, KO-08); the e-mail opt-in is one large
 * toggle row (KO-88).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <RequestReviewStep />
 * ```
 */
export function RequestReviewStep({
  client = kioskOperationsClient,
}: {
  readonly client?: Pick<KioskOperationsClient, "createPrescriptionRequest">;
} = {}) {
  const { t } = useTranslation();
  // A failed submission returns here instead of stopping on a dead-end error
  // screen. The screen's own guidance already played when the patient first
  // arrived, so without this a patient relying on the spoken guidance would get
  // no signal at all that their request was not sent.
  const hasSubmissionError = usePrescriptionFlowSelector(
    (snapshot) => snapshot.context.errorMessage !== undefined,
  );
  useAudio(
    hasSubmissionError
      ? "prescription-requestConfirmationError"
      : "prescription-requestReview",
  );
  const send = usePrescriptionFlowSend();
  const platform = usePlatform();

  const patientId = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.patientId,
  );
  const identification = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.identification,
  );
  const insuranceType = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.insuranceType,
  );
  const requestText = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.requestText,
  );
  const requestReason = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.requestReason,
  );
  const requestUrgency = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.requestUrgency,
  );
  const quarterlyEgkStatus = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.quarterlyEgkStatus,
  );
  const errorMessage = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.errorMessage,
  );
  const completionEmailRequested = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.completionEmailRequested,
  );
  const completionEmail = usePrescriptionFlowSelector(
    prescriptionFlowSelectors.completionEmail,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quarterlyCheckMode, setQuarterlyCheckMode] = useState<
    "warn" | "block" | "skip"
  >("warn");

  useEffect(
    function syncQuarterlyCheckMode() {
      if (identification?.kind !== "egk") return;
      let isCurrent = true;
      void (async () => {
        try {
          const settings = await platform.getPracticeSettings();
          if (isCurrent) {
            setQuarterlyCheckMode(
              settings.prescriptionRequestQuarterlyCheck ?? "warn",
            );
          }
        } catch (error) {
          void reportOperationalError({
            action: "prescription.review-settings-load",
            severity: "warning",
            source: "native",
            component: "RequestReviewStep",
            error,
          });
        }
      })();
      return () => {
        isCurrent = false;
      };
    },
    [identification, platform],
  );

  const isQuarterlyBlocked =
    quarterlyCheckMode === "block" && quarterlyEgkStatus === "warning";
  const trimmedRequest = requestText?.trim() ?? "";

  const handleSubmit = async (requestedCompletionEmail: string | undefined) => {
    if (!patientId || !identification || !insuranceType || !trimmedRequest) {
      send({
        type: "REQUEST_ERROR",
        message: t("self-service.prescription.request-review.submit-error"),
      });
      return;
    }

    send({ type: "SUBMIT_REQUEST" });
    setIsSubmitting(true);

    try {
      const outcome = await client.createPrescriptionRequest({
        patientId,
        insuranceType,
        identification,
        requestText: trimmedRequest,
        quarterlyWarning:
          identification.kind === "egk" && quarterlyEgkStatus === "warning",
        reason: requestReason,
        urgency: requestUrgency,
        completionEmail: requestedCompletionEmail,
      });
      if (outcome.kind === "policy-restricted") {
        send({ type: "REQUEST_POLICY_RESTRICTED" });
        return;
      }
      if (outcome.kind === "failed") {
        send({
          type: "REQUEST_ERROR",
          message: t("self-service.prescription.request-review.submit-error"),
        });
        return;
      }
      send({ type: "REQUEST_SUCCESS" });
    } catch {
      send({
        type: "REQUEST_ERROR",
        message: t("self-service.prescription.request-review.submit-error"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const form = useForm({
    defaultValues: {
      requested: completionEmailRequested,
      email: completionEmail ?? "",
    },
    validators: {
      onChange: S.toStandardSchemaV1(CompletionEmailChoice),
    },
    onSubmit: async ({ value }) => {
      await handleSubmit(value.requested ? value.email : undefined);
    },
  });

  const canSubmit = useStore(form.store, (state) => state.canSubmit);
  useStepPrimaryAction({
    label: isSubmitting
      ? t("self-service.prescription.request-review.submitting")
      : t("self-service.prescription.request-review.submit"),
    icon: Send,
    testId: "frontdesk-prescription-review-submit-btn",
    busy: isSubmitting,
    disabled:
      !trimmedRequest || !identification || isQuarterlyBlocked || !canSubmit,
    onClick: () => void form.handleSubmit(),
  });

  return (
    <KioskStep
      width="narrow"
      title={t("self-service.prescription.request-review.title-v2")}
      lead={t("self-service.prescription.request-review.subtitle")}
    >
      <form
        data-testid="frontdesk-prescription-review-form"
        className="flex min-w-0 flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        {identification?.kind === "manual" && <ManualIdentificationNotice />}

        {/* Error message from previous attempt */}
        {errorMessage && (
          <div
            data-testid="frontdesk-prescription-review-error"
            role="alert"
            className="flex items-center gap-4 rounded-2xl border border-destructive-subtle bg-destructive-soft p-5 text-foreground"
          >
            <span aria-hidden className="grid size-12 shrink-0 place-items-center rounded-xl bg-destructive-subtle text-destructive-ink">
              <CircleAlert className="size-6" />
            </span>
            <p className="m-0 text-lg leading-normal">{errorMessage}</p>
          </div>
        )}

        {/* Quarterly eGK warning/block banner */}
        {quarterlyEgkStatus === "warning" && quarterlyCheckMode !== "skip" && (
          <div
            data-testid="frontdesk-prescription-review-egk-warning"
            className={cn(
              "flex items-center gap-4 rounded-2xl border p-5 text-foreground",
              isQuarterlyBlocked
                ? "border-destructive-subtle bg-destructive-soft"
                : "border-warning/40 bg-warning-soft",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "grid size-12 shrink-0 place-items-center rounded-xl",
                isQuarterlyBlocked
                  ? "bg-destructive-subtle text-destructive-ink"
                  : "bg-warning/25 text-warning-ink",
              )}
            >
              {isQuarterlyBlocked ? (
                <ShieldAlert className="size-6" />
              ) : (
                <AlertTriangle className="size-6" />
              )}
            </span>
            <p className="m-0 text-lg leading-normal">
              {t("self-service.prescription.request-review.egk-warning")}
            </p>
          </div>
        )}

        <dl
          data-testid="frontdesk-prescription-review-meta"
          className="m-0 flex min-w-0 flex-col gap-4 rounded-3xl bg-primary-soft p-6"
        >
          {requestReason ? (
            <ReviewRow
              icon={ClipboardList}
              label={t("self-service.prescription.request-review.reason-label")}
            >
              {t(`self-service.prescription.medication-selection.reason-${requestReason}`)}
            </ReviewRow>
          ) : null}
          {requestUrgency ? (
            <ReviewRow
              icon={Gauge}
              label={t("self-service.prescription.request-review.urgency-label")}
            >
              {t(`self-service.prescription.medication-selection.urgency-${requestUrgency}`)}
            </ReviewRow>
          ) : null}
          {trimmedRequest ? (
            <ReviewRow
              icon={Pill}
              label={t("self-service.prescription.request-review.request-label")}
              testId="frontdesk-prescription-review-request"
            >
              <span className="whitespace-pre-line">{trimmedRequest}</span>
            </ReviewRow>
          ) : null}
        </dl>

        <form.Field name="requested">
          {(requestedField) => (
            <div className="flex min-w-0 flex-col gap-4">
              <button
                type="button"
                role="checkbox"
                aria-checked={requestedField.state.value}
                data-testid="frontdesk-prescription-completion-email-checkbox"
                onClick={() => {
                  const requested = !requestedField.state.value;
                  requestedField.handleChange(requested);
                  send({
                    type: "SET_COMPLETION_EMAIL_REQUESTED",
                    requested,
                  });
                  if (!requested) {
                    form.setFieldValue("email", "");
                  }
                }}
                className={cn(
                  "flex min-h-16 w-full items-center gap-4 rounded-2xl border-2 px-5 py-4 text-left text-lg transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/45",
                  requestedField.state.value
                    ? "border-primary bg-primary-subtle text-primary-ink"
                    : "border-border bg-card text-foreground hover:bg-foreground-soft",
                )}
              >
                <MailCheck aria-hidden className="size-6 shrink-0" />
                <span className="min-w-0 flex-1">
                  {t("self-service.prescription.request-review.completion-email-choice")}
                </span>
                {requestedField.state.value ? (
                  <SquareCheck aria-hidden className="size-8 shrink-0" />
                ) : (
                  <Square aria-hidden className="size-8 shrink-0" />
                )}
              </button>

              {requestedField.state.value && (
                <form.Field name="email">
                  {(emailField) => (
                    <KioskTextField
                      id="frontdesk-prescription-completion-email-address"
                      testId="frontdesk-prescription-completion-email-input"
                      errorTestId="frontdesk-prescription-completion-email-error"
                      label={t("self-service.prescription.request-review.completion-email-label")}
                      icon={Mail}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      placeholder={t("self-service.prescription.request-review.completion-email-placeholder")}
                      description={t("self-service.prescription.request-review.completion-email-hint-v2")}
                      value={emailField.state.value}
                      onBlur={emailField.handleBlur}
                      onChange={(value) => {
                        emailField.handleChange(value);
                        send({ type: "SET_COMPLETION_EMAIL", email: value });
                      }}
                      error={
                        emailField.state.meta.errors.length > 0
                          ? t("self-service.prescription.request-review.completion-email-invalid")
                          : undefined
                      }
                    />
                  )}
                </form.Field>
              )}
            </div>
          )}
        </form.Field>
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </KioskStep>
  );
}

function ReviewRow({
  icon: Icon,
  label,
  testId,
  children,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly testId?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-start gap-4">
      <span aria-hidden className="grid size-12 shrink-0 place-items-center rounded-2xl bg-primary-subtle text-primary-ink [&_svg]:size-6">
        <Icon />
      </span>
      <div className="min-w-0">
        <dt className="text-base text-muted-foreground">{label}</dt>
        <dd data-testid={testId} className="m-0 text-xl font-semibold break-words text-foreground">
          {children}
        </dd>
      </div>
    </div>
  );
}
